// @givmo/app-admin — join-request display helpers (EP-SUITE2). Tolerant decode over the
// CompanyJoinRequestOut shape from @givmo/api-client (types from the SDK, never re-modeled). Never
// throws — every helper degrades to a safe fallback so a partial/odd row still renders.

import type { CompanyJoinRequestOut } from '@givmo/api-client';
import { productLabel } from '../../lib/app-names';

/** The wire status of a PENDING (still-adjudicable) request. */
export const JOIN_REQUEST_STATUS_PENDING = 'PENDING';

/**
 * The requester's display name: "First Last" when present, else the work email, else a neutral
 * fallback. Trims and collapses a lone first/last. Never returns an empty string.
 */
export function requesterName(req: CompanyJoinRequestOut): string {
  const first = (req.requester_first_name ?? '').trim();
  const last = (req.requester_last_name ?? '').trim();
  const full = `${first} ${last}`.trim();
  if (full.length > 0) return full;
  const email = (req.requester_email ?? '').trim();
  if (email.length > 0) return email;
  return 'A teammate';
}

/** The requester's email, or '' when absent (the row omits the secondary line). */
export function requesterEmail(req: CompanyJoinRequestOut): string {
  return (req.requester_email ?? '').trim();
}

/**
 * The app the requester said they need (`requested_app`, EP-SUITE8), named, or an em dash when they
 * said nothing. Never the legacy `requested_product`: that was the sign-up screen's stamp on every
 * request, not the person's choice (the agreed OM-57 wire, §3). Tolerant of a backend before OM-57,
 * which sends no key.
 */
export function requestedAppLabel(req: CompanyJoinRequestOut): string {
  return productLabel(requestedApp(req));
}

/** The requester's `requested_app` slug, or `null` when they said nothing (or the key is absent). */
export function requestedApp(req: CompanyJoinRequestOut | null): string | null {
  const value: unknown = req?.requested_app;
  if (typeof value !== 'string') return null;
  const slug = value.trim();
  return slug.length > 0 ? slug : null;
}

/**
 * A short, locale-formatted request date from an ISO string, or '' when absent/unparseable (tolerant —
 * a bad date never throws). Date-only (the inbox does not need the time).
 */
export function formatRequestDate(iso: string | null | undefined): string {
  if (!iso || iso.length === 0) return '';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  return new Date(ts).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Whether the request is still PENDING (i.e. the Owner can approve/reject it). */
export function isPendingRequest(req: CompanyJoinRequestOut): boolean {
  return req.status === JOIN_REQUEST_STATUS_PENDING;
}

/** A display label for a request's status ('Pending' / 'Approved' / 'Rejected' / the raw value). */
export function statusLabel(status: string | null | undefined): string {
  const s = (status ?? '').trim();
  if (s.length === 0) return '—';
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}
